// Utilitaires pour la page d'accueil

export const getStatusBadgeColor = (count, type) => {
  if (count === 0) return "bg-gray-100 text-gray-800";

  switch (type) {
    case "warning":
      return "bg-yellow-100 text-yellow-800";
    case "failed":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export const getGatewayStatusColor = (ratio) => {
  if (!ratio || ratio === "-" || ratio === null) return "bg-gray-100 text-gray-800";
  
  const [online, total] = ratio.split("/").map(Number);
  if (total === 0 || isNaN(online) || isNaN(total)) return "bg-gray-100 text-gray-800";

  const percentage = (online / total) * 100;
  if (percentage === 100) return "bg-green-100 text-green-800";
  if (percentage >= 50) return "bg-yellow-100 text-yellow-800";
  return "bg-red-100 text-red-800";
};

export const formatLastCheck = (timestamp) => {
  if (!timestamp) return "Jamais";

  const now = new Date();
  const date = new Date(timestamp);
  const diffInMs = now - date;
  const diffInMinutes = Math.floor(diffInMs / 60000);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInMinutes < 1) return "À l'instant";
  if (diffInMinutes < 60) return `${diffInMinutes} min`;
  if (diffInHours < 24) return `${diffInHours}h`;
  if (diffInDays < 30) return `${diffInDays}j`;

  // Pour les dates très anciennes, afficher la date de création
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

export const formatInstallationDate = (date) => {
  if (!date || typeof date !== "string") return "";

  const normalizedDate = date.trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(normalizedDate);
  if (!match) return "";

  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
};

export const formatInstalledSince = (date) => {
  const formattedDate = formatInstallationDate(date);
  return formattedDate ? `installé depuis le ${formattedDate}` : "";
};

export const getPerformanceDisplay = (goodPercentages) => {
  const today = goodPercentages.today;
  const d1 = goodPercentages.d1;
  const d3 = goodPercentages.d3;
  const d15 = goodPercentages.d15;
  const d30 = goodPercentages.d30;

  // Calcul des variations
  const variation1d = today - d1;
  const variation3d = today - d3;
  const variation15d = today - d15;
  const variation30d = today - d30;

  const getVariationColor = (variation) => {
    if (variation > 0) return "text-green-600";
    if (variation < 0) return "text-red-600";
    return "text-gray-500";
  };

  const getVariationIcon = (variation) => {
    if (variation > 0) return "↗";
    if (variation < 0) return "↘";
    return "→";
  };

  const formatVariation = (variation) => {
    const abs = Math.abs(variation);
    const sign = variation >= 0 ? "+" : "-";
    return `${sign}${abs.toFixed(1)}%`;
  };

  return {
    today,
    variations: [
      {
        period: "1j",
        value: variation1d,
        color: getVariationColor(variation1d),
        icon: getVariationIcon(variation1d),
        formatted: formatVariation(variation1d),
      },
      {
        period: "3j",
        value: variation3d,
        color: getVariationColor(variation3d),
        icon: getVariationIcon(variation3d),
        formatted: formatVariation(variation3d),
      },
      {
        period: "15j",
        value: variation15d,
        color: getVariationColor(variation15d),
        icon: getVariationIcon(variation15d),
        formatted: formatVariation(variation15d),
      },
      {
        period: "30j",
        value: variation30d,
        color: getVariationColor(variation30d),
        icon: getVariationIcon(variation30d),
        formatted: formatVariation(variation30d),
      },
    ],
  };
};

// Type d'hôtel pour le tri : "lunishower" | "greenmiles" | "both"
export const getHotelTypeSortKey = (hotel) => {
  const ls = Boolean(hotel.has_lunishower);
  const gm = Boolean(hotel.has_greenmiles);
  if (ls && gm) return "both";
  if (ls) return "lunishower";
  if (gm) return "greenmiles";
  return "";
};

// Fonctions de tri pour les hôtels
export const sortHotels = (hotels, sortColumn, sortDirection) => {
  if (!sortColumn) return hotels;

  const typeOrder = { lunishower: 0, both: 1, greenmiles: 2, "": 3 };

  return [...hotels].sort((a, b) => {
    let aValue, bValue;

    switch (sortColumn) {
      case "name":
        aValue = a.name?.toLowerCase() || "";
        bValue = b.name?.toLowerCase() || "";
        break;
      case "type":
        aValue = typeOrder[getHotelTypeSortKey(a)] ?? 99;
        bValue = typeOrder[getHotelTypeSortKey(b)] ?? 99;
        break;
      case "tag":
        aValue = (a.tag || "").toLowerCase();
        bValue = (b.tag || "").toLowerCase();
        break;
      case "nb_greenmiles_stays":
        aValue = parseInt(a.nb_greenmiles_stays) || 0;
        bValue = parseInt(b.nb_greenmiles_stays) || 0;
        break;
      case "performance":
        // Tri par performance actuelle (today) - s'assurer que c'est un nombre
        aValue = parseFloat(a.good_percentages?.today) || 0;
        bValue = parseFloat(b.good_percentages?.today) || 0;
        break;
      case "room_status":
        // Tri par nombre total d'alertes (warning + failed)
        aValue = parseInt(a.room_status?.warning || 0) + parseInt(a.room_status?.failed || 0);
        bValue = parseInt(b.room_status?.warning || 0) + parseInt(b.room_status?.failed || 0);
        break;
      case "room_count":
        aValue = parseInt(a.room_count) || 0;
        bValue = parseInt(b.room_count) || 0;
        break;
      case "installation_date":
        aValue = a.installationDate ? new Date(a.installationDate).getTime() : 0;
        bValue = b.installationDate ? new Date(b.installationDate).getTime() : 0;
        break;
      case "gateways":
        // Tri par ratio de gateways online
        const aTotal = parseInt(a.gateways?.total) || 0;
        const bTotal = parseInt(b.gateways?.total) || 0;
        const aOnline = parseInt(a.gateways?.online) || 0;
        const bOnline = parseInt(b.gateways?.online) || 0;
        aValue = aTotal > 0 ? aOnline / aTotal : 0;
        bValue = bTotal > 0 ? bOnline / bTotal : 0;
        break;
      case "last_check":
        // Tri par date de dernière activité
        aValue = a.last_check ? new Date(a.last_check).getTime() : 0;
        bValue = b.last_check ? new Date(b.last_check).getTime() : 0;
        break;
      default:
        return 0;
    }

    // Tri alphabétique pour les chaînes
    if (typeof aValue === "string" && typeof bValue === "string") {
      const comparison = aValue.localeCompare(bValue);
      return sortDirection === "asc" ? comparison : -comparison;
    }

    // Tri numérique pour les nombres
    if (sortDirection === "asc") {
      return aValue - bValue;
    } else {
      return bValue - aValue;
    }
  });
};

export const getNextSortDirection = (currentColumn, newColumn, currentDirection) => {
  if (currentColumn !== newColumn) {
    return "asc";
  }
  return currentDirection === "asc" ? "desc" : "asc";
};

export const getSortIcon = (column, currentColumn, currentDirection) => {
  if (column !== currentColumn) {
    return "↕️";
  }
  return currentDirection === "asc" ? "↑" : "↓";
};
